export type LinearCoordinate = [longitude: number, latitude: number];

export type LinearMapFeature = {
  key: string;
  featureId: string;
  mapPackId: string;
  sourceLayer: "water";
  name: string;
  named: boolean;
  kind: string;
  lines: LinearCoordinate[][];
};

let decoderPromise: Promise<{ VectorTile: typeof import("@mapbox/vector-tile").VectorTile; PbfReader: typeof import("pbf").PbfReader }> | null = null;

function getDecoder() {
  if (!decoderPromise) {
    decoderPromise = Promise.all([import("@mapbox/vector-tile"), import("pbf")])
      .then(([vectorTile, pbf]) => ({ VectorTile: vectorTile.VectorTile, PbfReader: pbf.PbfReader }));
  }
  return decoderPromise;
}

function text(value: unknown) {
  return typeof value === "string" ? value.trim() : "";
}

function normalizeKey(value: string) {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("es")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function waterKind(properties: Record<string, unknown>) {
  return (text(properties.kind_detail) || text(properties.kind) || text(properties.waterway) || "water").toLowerCase();
}

function genericWaterName(kind: string) {
  if (kind === "river") return "Rio sin nombre";
  if (["stream", "ditch", "drain"].includes(kind)) return "Quebrada / curso de agua sin nombre";
  if (kind === "canal") return "Canal sin nombre";
  return "Curso de agua sin nombre";
}

function normalizeLineCoordinates(value: unknown): LinearCoordinate[][] {
  if (!Array.isArray(value)) return [];
  // LineString coordinates: [[lon, lat], ...]
  if (value.length && Array.isArray(value[0]) && typeof value[0]?.[0] === "number") {
    const line = value
      .filter((coordinate): coordinate is [number, number] => Array.isArray(coordinate)
        && coordinate.length >= 2
        && Number.isFinite(Number(coordinate[0]))
        && Number.isFinite(Number(coordinate[1])))
      .map((coordinate) => [Number(coordinate[0]), Number(coordinate[1])] as LinearCoordinate);
    return line.length >= 2 ? [line] : [];
  }
  // MultiLineString coordinates: [[[lon, lat], ...], ...]
  const output: LinearCoordinate[][] = [];
  for (const child of value) output.push(...normalizeLineCoordinates(child));
  return output;
}

function featureRecord(
  feature: {
    id?: number;
    properties: Record<string, unknown>;
    toGeoJSON: (x: number, y: number, z: number) => { geometry?: { type?: string; coordinates?: unknown } | null };
  },
  x: number,
  y: number,
  z: number,
  mapPackId: string,
): LinearMapFeature | null {
  const geojson = feature.toGeoJSON(x, y, z);
  if (geojson.geometry?.type !== "LineString" && geojson.geometry?.type !== "MultiLineString") return null;
  const lines = normalizeLineCoordinates(geojson.geometry.coordinates);
  if (!lines.length) return null;

  const kind = waterKind(feature.properties);
  const explicitName = text(feature.properties["name:es"]) || text(feature.properties.name);
  const name = explicitName || genericWaterName(kind);
  const stableBase = feature.id !== undefined
    ? String(feature.id)
    : `${normalizeKey(name)}-${lines[0][0][1].toFixed(5)}-${lines[0][0][0].toFixed(5)}`;
  return {
    key: `water:${stableBase}`,
    featureId: `mapline-${mapPackId}-water-${stableBase}`,
    mapPackId,
    sourceLayer: "water",
    name,
    named: Boolean(explicitName),
    kind,
    lines,
  };
}

export async function extractWaterLineFeaturesFromMvt(
  data: ArrayBuffer,
  x: number,
  y: number,
  z: number,
  mapPackId: string,
) {
  const { VectorTile, PbfReader } = await getDecoder();
  const tile = new VectorTile(new PbfReader(new Uint8Array(data)));
  const layer = tile.layers.water;
  if (!layer) return [] as LinearMapFeature[];

  const output: LinearMapFeature[] = [];
  for (let index = 0; index < layer.length; index += 1) {
    const feature = layer.feature(index);
    const record = featureRecord(feature as never, x, y, z, mapPackId);
    if (record) output.push(record);
  }
  return output;
}
